import { Injectable, Provider, Type } from "@angular/core";

export type SideSpaceOptions = {
  size?: "xl" | "md" | "sm";
  titleTemplate?: string;
  flags?: {
    pinnable?: boolean;
    resizable?: boolean;
    floatable?: boolean;
  };
  providers?: Array<Provider>;
};

export interface SidespacePresenter {
  present(
    component: Type<any>,
    data?: Record<string, any>,
    options?: SideSpaceOptions
  ): void;

  presentUrl({ url }: { url: string }): void;

  open<T>(options?: SideSpaceOptions): Promise<T>;

  close(result?: any): void;

  setSize(size: NonNullable<SideSpaceOptions["size"]>): void;

  pin(): void;

  unPin(): void;

  lock(): void;

  unlock(): void;
}

@Injectable()
export class SidespaceService {
  presenter: SidespacePresenter | undefined;

  #beforeCloseHandler: (() => boolean | Promise<boolean>) | undefined;

  install(presenter: SidespacePresenter): void {
    this.presenter = presenter;
  }

  setBeforeCloseHandler(
    handler: (() => boolean | Promise<boolean>) | undefined
  ): void {
    this.#beforeCloseHandler = handler;
  }

  async requestClose(): Promise<boolean> {
    if (!this.#beforeCloseHandler) {
      return true;
    }

    return await Promise.resolve(this.#beforeCloseHandler());
  }

  async open<T>(options?: SideSpaceOptions): Promise<T | undefined> {
    return await (this.presenter?.open<T | undefined>(options) ||
      Promise.resolve<T | undefined>(undefined));
  }

  present(
    component: Type<any>,
    data?: Record<string, any>,
    options?: SideSpaceOptions
  ): void {
    this.presenter?.present(component, data, options);
  }

  async presentAndOpen<T>(
    component: Type<any>,
    data?: Record<string, any>,
    options?: SideSpaceOptions
  ): Promise<T | undefined> {
    this.present(component, data, options);
    return await this.open(options);
  }

  async presenteAndOpenIframe({
    url,
    options
  }: {
    url: string;
    options?: SideSpaceOptions;
  }): Promise<void> {
    this.presenter?.presentUrl({ url });
    return await this.open(options);
  }

  close(result?: any): void {
    this.presenter?.close(result);
  }

  setSize(size: NonNullable<SideSpaceOptions["size"]>): void {
    this.presenter?.setSize(size);
  }

  pin(): void {
    this.presenter?.pin();
  }

  unPin(): void {
    this.presenter?.unPin();
  }

  lock(): void {
    this.presenter?.lock();
  }

  unlock(): void {
    this.presenter?.unlock();
  }
}
